import React, { useState, useEffect } from "react";
import { Table, Spinner, Button, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { TiTick } from "react-icons/ti";
import instance from "../../services/baseService";
import toast from "react-hot-toast";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import DynamicModal from "../Employees/DynamicModal";
import { BsInfoCircle } from "react-icons/bs";
function PortWiseTab({ scripNo, portCodeId, scripDate, gstNo, portCode, iecCode, tableHeaders, tableData, loading, callback, clientId, reportId, reportData, totalCount, totalAmount, currentOrder, totalSiblings, portCodeData, onNavigateToPortCode }) {
    const [loadingData, setLoadingData] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [showMoveConfirmModal, setShowMoveConfirmModal] = useState(false);
    const [showMoveBottomConfirmModal, setShowMoveBottomConfirmModal] = useState(false);
    const [moveLoading, setMoveLoading] = useState(false);
    // Determine if Move to Top feature should be enabled
    // Hide when: scripNo exists, manual_brc is true, status is 0 or 1, only 1 sibling, or viewing parent (order=1)
    const showMoveToTop = !scripNo &&
        !reportData?.manual_brc &&
        (portCodeData?.status === 0 || portCodeData?.status === 1) &&
        totalSiblings > 1 &&
        currentOrder > 1;

    // Determine if Move to Bottom feature should be enabled
    // Hide when: scripNo exists, manual_brc is true, status is not 0 or 1, only 1 sibling, or viewing last order
    const showMoveToBottom = !scripNo &&
        !reportData?.manual_brc &&
        (portCodeData?.status === 0 || portCodeData?.status === 1) &&
        totalSiblings > 1 &&
        currentOrder < totalSiblings;

    // Combined condition for showing checkboxes (either Move to Top or Move to Bottom is available)
    const showMoveOptions = showMoveToTop || showMoveToBottom;

    useEffect(() => {
        setSelectedRows([]);
    }, [tableData, portCodeId]);

    const formatCurrency = (amount) => {
        if (!amount || amount === null || amount === undefined) return '₹0';
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        return `₹${numAmount.toLocaleString('en-IN')}`;
    };

    const formatISODate = (d) => {
        return new Date(d).toLocaleDateString("en-GB", { timeZone: "UTC" });
    };

    // =============================================================================
    // SB NUMBER EXPIRY HIGHLIGHTING LOGIC
    // Subsidy expires exactly 1 year from sb_date (handles leap years automatically)
    // - 0-180 days elapsed: Normal (no highlight)
    // - 181-299 days elapsed: Warning (light yellow)
    // - 300+ days elapsed (before expiry): Danger (light red)
    // - After expiry date: Expired (light red)
    // =============================================================================
    const WARNING_THRESHOLD = 181;  // Days after which warning starts
    const DANGER_THRESHOLD = 300;   // Days after which danger starts

    const getExpiryStatus = (sbDate) => {
        // Default safe return for invalid/missing dates
        const defaultReturn = {
            status: 'normal',
            daysElapsed: 0,
            daysRemaining: 365,
            expiryDate: null,
            sbDate: null
        };

        if (!sbDate) return defaultReturn;

        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);  // Reset time for accurate day calculation

            const sbDateObj = new Date(sbDate);

            // Validate parsed date
            if (isNaN(sbDateObj.getTime())) {
                console.warn('Invalid sb_date:', sbDate);
                return defaultReturn;
            }

            sbDateObj.setHours(0, 0, 0, 0);

            // =============================================================================
            // LEAP YEAR HANDLING:
            // Instead of using a fixed 365 days, we calculate the exact expiry date
            // by adding 1 year to sb_date. JavaScript Date automatically handles leap years.
            // Example: 
            //   - sb_date: 2024-02-29 → expiry: 2025-02-28 (leap year to non-leap year)
            //   - sb_date: 2023-03-01 → expiry: 2024-03-01 (includes Feb 29, 2024)
            // =============================================================================
            const expiryDate = new Date(sbDateObj);
            expiryDate.setFullYear(expiryDate.getFullYear() + 1);

            // Calculate days elapsed since sb_date
            const msPerDay = 1000 * 60 * 60 * 24;
            const daysElapsed = Math.floor((today - sbDateObj) / msPerDay);

            // Calculate days remaining until expiry (can be negative if expired)
            const daysRemaining = Math.floor((expiryDate - today) / msPerDay);

            // Determine status based on days elapsed and expiry
            let status = 'normal';
            if (daysRemaining <= 0) {
                // Already expired
                status = 'expired';
            } else if (daysElapsed >= DANGER_THRESHOLD) {
                // 300+ days elapsed, approaching expiry
                status = 'danger';
            } else if (daysElapsed >= WARNING_THRESHOLD) {
                // 181-299 days elapsed
                status = 'warning';
            }

            return {
                status,
                daysElapsed,
                daysRemaining: Math.max(0, daysRemaining),  // Ensure non-negative for display
                expiryDate,
                sbDate: sbDateObj
            };

        } catch (error) {
            // Catch any unexpected errors to prevent frontend crash
            console.error('Error calculating expiry status:', error);
            return defaultReturn;
        }
    };

    const getExpiryStyle = (status) => {
        switch (status) {
            case 'warning':
                return { backgroundColor: '#fff3cd', color: '#fd7e14' };  // Light yellow
            case 'danger':
                return { backgroundColor: '#f8d7da', color: '#dc3545' };  // Light red
            case 'expired':
                return { backgroundColor: '#f12536ff', color: '#ffffffff' };  // Dark red
            default:
                return {};
        }
    };

    const getExpiryTooltipText = (expiryInfo) => {
        const { status, daysRemaining, expiryDate } = expiryInfo;

        try {
            if (status === 'expired') {
                // Show the actual expiry date (1 year from sb_date)
                const expiryDateStr = expiryDate
                    ? expiryDate.toLocaleDateString("en-GB")
                    : 'unknown date';
                return `Subsidy expired on ${expiryDateStr}`;
            }

            return `Subsidy going to expire in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`;
        } catch (error) {
            console.error('Error generating tooltip text:', error);
            return 'Expiry information unavailable';
        }
    };

    // Reusable component for SB Number cell with expiry highlighting
    const SBNumberCell = ({ sbNo, sbDate, rowSpan }) => {
        const expiryInfo = getExpiryStatus(sbDate);
        const expiryStyle = getExpiryStyle(expiryInfo.status);
        const showIcon = expiryInfo.status !== 'normal';

        const cellStyle = {
            whiteSpace: 'nowrap',
            ...expiryStyle
        };

        const renderTooltip = (props) => (
            <Tooltip id={`tooltip-${sbNo}`} {...props} className="fw-bold">
                {getExpiryTooltipText(expiryInfo)}
            </Tooltip>
        );

        const cellContent = (
            <span className="d-flex align-items-center justify-content-between w-100 gap-2">
                {sbNo}
                {showIcon && (
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 100, hide: 200 }}
                        overlay={renderTooltip}
                    >
                        <span style={{ cursor: 'pointer', display: 'inline-flex' }}>
                            <BsInfoCircle size={14} />
                        </span>
                    </OverlayTrigger>
                )}
            </span>
        );

        if (rowSpan) {
            return (
                <td className="text-left" style={cellStyle} rowSpan={rowSpan}>
                    {cellContent}
                </td>
            );
        }

        return (
            <td className="text-left" style={cellStyle}>
                {cellContent}
            </td>
        );
    };

    const handleDeletePort = async () => {
        try {
            setLoadingData(true);
            const response = await instance.post(
                `/api/v1/reconciliation/port-code/delete/${clientId}/${reportId}/${portCodeId}`,
            );

            toast.success(response.data.message);
            callback();
        } catch (error) {
            console.error("Delete API Error:", error);
            toast.error(error.response?.data?.message || "Delete failed");
        } finally {
            setLoadingData(false);
        }
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            // Select all row IDs (MongoDB _id)
            const allIds = tableData.map(row => row.id);
            setSelectedRows(allIds);
        } else {
            setSelectedRows([]);
        }
    };

    const handleSelectRow = (rowId, checked) => {
        if (checked) {
            setSelectedRows(prev => [...prev, rowId]);
        } else {
            setSelectedRows(prev => prev.filter(id => id !== rowId));
        }
    };

    const isAllSelected = tableData.length > 0 && selectedRows.length === tableData.length;
    const isIndeterminate = selectedRows.length > 0 && selectedRows.length < tableData.length;


    const handleMoveToTop = async () => {
        try {
            setMoveLoading(true);
            const response = await instance.post(
                `/api/v1/reconciliation/port-code/move-to-top/${clientId}/${reportId}`,
                {
                    record_ids: selectedRows,
                    source_port_code_id: parseInt(portCodeId)  // Ensure integer
                }
            );

            if (response.data.type === "success") {
                toast.success(response.data.message || "Records moved successfully");
                setShowMoveConfirmModal(false);
                setSelectedRows([]);

                // Navigate to target PortCode
                const targetPortCodeId = response.data.data?.target_port_code_id;
                if (targetPortCodeId && onNavigateToPortCode) {
                    onNavigateToPortCode(targetPortCodeId);
                } else {
                    // Fallback: refresh current view
                    callback();
                }
            } else {
                // Handle non-success response that didn't throw error
                toast.error(response.data.message || "Move to top failed");
            }
        } catch (error) {
            console.error("Move to Top API Error:", error);
            toast.error(error.response?.data?.message || "Move to top failed");
        } finally {
            setMoveLoading(false);
        }
    };

    const handleMoveToBottom = async () => {
        try {
            setMoveLoading(true);
            const response = await instance.post(
                `/api/v1/reconciliation/port-code/move-to-bottom/${clientId}/${reportId}`,
                {
                    record_ids: selectedRows,
                    source_port_code_id: parseInt(portCodeId)
                }
            );

            if (response.data.type === "success") {
                toast.success(response.data.message || "Records moved successfully");
                setShowMoveBottomConfirmModal(false);
                setSelectedRows([]);

                // Navigate to target PortCode
                const targetPortCodeId = response.data.data?.target_port_code_id;
                if (targetPortCodeId && onNavigateToPortCode) {
                    onNavigateToPortCode(targetPortCodeId);
                } else {
                    // Fallback: refresh current view
                    callback();
                }
            } else {
                toast.error(response.data.message || "Move to bottom failed");
            }
        } catch (error) {
            console.error("Move to Bottom API Error:", error);
            toast.error(error.response?.data?.message || "Move to bottom failed");
        } finally {
            setMoveLoading(false);
        }
    };

    const extractIFSC = (brcNo) => {
        if (!brcNo || brcNo.length < 11) return 'N/A';
        // First 4 (Bank) + 5th char (0) + Next 6 (Branch) = 11 characters
        return brcNo.substring(0, 11);
    };

    return (
        <>
            <div className="mb-4 d-flex flex-wrap  align-items-center gap-3 justify-content-between">
                {/* <RiDeleteBin5Line
                    size={20}
                    color="red"
                    className="cursor-pointer"
                    title="Delete Port Code"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleDeletePort()}
                /> */}

                {reportData.manual_brc ? (
                    <Button
                        variant="danger"
                        size="sm"
                        className="fw-bold"
                        onClick={() => handleDeletePort()}
                        style={{ width: "100px" }}
                    >
                        Delete
                    </Button>
                ) : null}

                {showMoveToTop && (
                    <Button
                        variant="primary"
                        size="sm"
                        className="fw-bold d-flex align-items-center gap-1"
                        onClick={() => setShowMoveConfirmModal(true)}
                        disabled={selectedRows.length === 0 || loadingData || moveLoading}
                    >
                        <FaArrowUp />
                        Move to Top ({selectedRows.length})
                    </Button>
                )}
                {showMoveToBottom && (
                    <Button
                        variant="primary"
                        size="sm"
                        className="fw-bold d-flex align-items-center gap-1"
                        onClick={() => setShowMoveBottomConfirmModal(true)}
                        disabled={selectedRows.length === 0 || loadingData || moveLoading}
                    >
                        <FaArrowDown />
                        Move to Bottom ({selectedRows.length})
                    </Button>
                )}
                {!reportData.manual_brc && totalSiblings > 1 && (
                    <div>
                        <strong className="me-2">Part:</strong>
                        <span>{currentOrder} of {totalSiblings}</span>
                    </div>
                )}
                <div>
                    <strong className="me-2">Total Count:</strong>
                    <span>{totalCount ? totalCount : "N/A"}</span>
                </div>
                <div>
                    <strong className="me-2">Total Amount:</strong>
                    <span>{totalAmount ? totalAmount : "N/A"}</span>
                </div>
                <div>
                    <strong className="me-2">Scrip No:</strong>
                    <span>{scripNo ? scripNo : "N/A"}</span>
                </div>
                <div>
                    <strong className="me-2">Scrip Date:</strong>
                    <span>{scripDate ? formatISODate(scripDate) : "N/A"}</span>
                </div>
                <div>
                    <strong className="me-2">GST No:</strong>
                    <span>{gstNo ? gstNo : "N/A"}</span>
                </div>
                <div>
                    <strong className="me-2">Port Code:</strong>
                    <span>{portCode ? portCode : "N/A"}</span>
                </div>

                <div>
                    <strong className="me-2">IEC No:</strong>
                    <span>{iecCode ? iecCode : "N/A"}</span>
                </div>
            </div>
            <div className="overflow-auto" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    </div>
                ) : (
                    <Table className="core-ui-table seperate-border">
                        <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 4, backgroundColor: '#f8f9fa' }}>
                            <tr>
                                {/* Select All Checkbox - only show when Move to Top is enabled */}
                                {showMoveOptions && (
                                    <th
                                        className="text-center"
                                        style={{
                                            whiteSpace: 'nowrap',
                                            width: '50px',
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 3,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Form.Check
                                            type="checkbox"
                                            className="sticky-checkbox"
                                            style={{ margin: 0 }}
                                            checked={isAllSelected}
                                            ref={input => {
                                                if (input) input.indeterminate = isIndeterminate;
                                            }}
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                            disabled={tableData.length === 0}
                                        />
                                    </th>
                                )}
                                {tableHeaders.map((header, index) => (
                                    <th key={index} style={{ whiteSpace: 'nowrap' }}>{header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.length > 0 ? (
                                tableData.map((row, rowIndex) => {
                                    const brcData = row.brc_data || [];
                                    const brcCount = brcData.length || 1; // At least 1 row even if empty

                                    return (
                                        <React.Fragment key={rowIndex}>
                                            {brcCount === 0 || brcData.length === 0 ? (
                                                // No BRC data - show single row with N/A
                                                <tr>
                                                    {showMoveOptions && (
                                                        <td
                                                            className="text-center"
                                                            style={{
                                                                position: 'sticky',
                                                                left: 0,
                                                                width: '50px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                zIndex: 2
                                                            }}
                                                        >
                                                            <Form.Check
                                                                type="checkbox"
                                                                className="sticky-checkbox"
                                                                style={{ margin: 0 }}
                                                                checked={selectedRows.includes(row.id)}
                                                                onChange={(e) => handleSelectRow(row.id, e.target.checked)}
                                                            />
                                                        </td>
                                                    )}
                                                    <td className="text-left" style={{ whiteSpace: 'nowrap' }}>{rowIndex + 1}</td>
                                                    <SBNumberCell sbNo={row.sbNo} sbDate={row.sbDate} />
                                                    <td className="text-left" style={{ whiteSpace: 'nowrap' }}>{row.sbDate}</td>
                                                    <td className="text-left" style={{ whiteSpace: 'nowrap' }}>{row.scrollNo}</td>
                                                    <td className="text-left" style={{ whiteSpace: 'nowrap' }}>{row.status}</td>
                                                    <td className="text-left" style={{ whiteSpace: 'nowrap' }}>{row.type}</td>
                                                    <td className="text-left" style={{ whiteSpace: 'nowrap' }}>{formatCurrency(row.amount)}</td>
                                                    <td className="text-left text-muted" style={{ whiteSpace: 'nowrap' }}>N/A</td>
                                                    <td className="text-left text-muted" style={{ whiteSpace: 'nowrap' }}>N/A</td>
                                                    <td className="text-left text-muted" style={{ whiteSpace: 'nowrap' }}>N/A</td>
                                                    <td className="text-left text-muted" style={{ whiteSpace: 'nowrap' }}>N/A</td>
                                                    <td className="text-left text-muted" style={{ whiteSpace: 'nowrap' }}>N/A</td>
                                                </tr>
                                            ) : (
                                                // Has BRC data - show multiple rows with rowSpan for IceGate columns
                                                brcData.map((brc, brcIndex) => (
                                                    <tr key={`${rowIndex}-${brcIndex}`}>
                                                        {/* IceGate columns - only show in first row with rowSpan */}
                                                        {brcIndex === 0 && (
                                                            <>
                                                                {showMoveOptions && (
                                                                    <td
                                                                        className="text-center"
                                                                        rowSpan={brcCount}
                                                                        style={{
                                                                            whiteSpace: 'nowrap',
                                                                            position: 'sticky',
                                                                            left: 0,
                                                                            zIndex: 2,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                        }}
                                                                    >
                                                                        <Form.Check
                                                                            type="checkbox"
                                                                            className="sticky-checkbox"
                                                                            style={{ margin: 0 }}
                                                                            checked={selectedRows.includes(row.id)}
                                                                            onChange={(e) => handleSelectRow(row.id, e.target.checked)}
                                                                        />
                                                                    </td>
                                                                )}
                                                                <td className="text-left" style={{ whiteSpace: 'nowrap' }} rowSpan={brcCount}>{rowIndex + 1}</td>
                                                                <SBNumberCell sbNo={row.sbNo} sbDate={row.sbDate} rowSpan={brcCount} />
                                                                <td className="text-left" style={{ whiteSpace: 'nowrap' }} rowSpan={brcCount}>{row.sbDate}</td>
                                                                <td className="text-left" style={{ whiteSpace: 'nowrap' }} rowSpan={brcCount}>{row.scrollNo}</td>
                                                                <td className="text-left" style={{ whiteSpace: 'nowrap' }} rowSpan={brcCount}>{row.status}</td>
                                                                <td className="text-left" style={{ whiteSpace: 'nowrap' }} rowSpan={brcCount}>{row.type}</td>
                                                                <td className="text-left" style={{ whiteSpace: 'nowrap' }} rowSpan={brcCount}>{formatCurrency(row.amount)}</td>
                                                            </>
                                                        )}
                                                        {/* BRC/DGFT columns - show for each brc_data entry */}
                                                        <td className="text-left" style={{ whiteSpace: 'nowrap' }}>{formatCurrency(brc.fob)}</td>
                                                        <td className="text-left" style={{ whiteSpace: 'nowrap' }}>{brc.brc_no || ''}</td>
                                                        <td className="text-left" style={{ whiteSpace: 'nowrap' }}>{brc.brc_date || ''}</td>
                                                        <td className="text-left" style={{ whiteSpace: 'nowrap' }}>{formatCurrency(brc.brc_realised_value)}</td>
                                                        <td className="text-left" style={{ whiteSpace: 'nowrap' }}>{extractIFSC(brc.brc_no)}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={showMoveOptions ? tableHeaders.length + 1 : tableHeaders.length} className="text-center py-4">
                                        No data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </div>
            <DynamicModal
                show={showMoveConfirmModal}
                onHide={() => setShowMoveConfirmModal(false)}
                title="Confirm Move to Top"
                size="md"
                loading={moveLoading}
                buttons={[
                    {
                        label: "Cancel",
                        variant: "secondary",
                        action: "close",
                        disabled: moveLoading
                    },
                    {
                        label: "Move",
                        variant: "primary",
                        action: "custom",
                        onClick: handleMoveToTop,
                        disabled: moveLoading,
                        loadingLabel: "Moving..."
                    }
                ]}
            >
                <div className="text-center py-3">
                    <p className="mb-2">
                        Are you sure you want to move <strong>{selectedRows.length}</strong> SB number(s) to the top?
                    </p>
                    <p className="text-muted small mb-0">
                        The selected records will be moved to the highest available PortCode in the hierarchy.
                    </p>
                </div>
            </DynamicModal>

            {/* Move to Bottom Confirmation Modal */}
            <DynamicModal
                show={showMoveBottomConfirmModal}
                onHide={() => setShowMoveBottomConfirmModal(false)}
                title="Confirm Move to Bottom"
                size="md"
                loading={moveLoading}
                buttons={[
                    {
                        label: "Cancel",
                        variant: "secondary",
                        action: "close",
                        disabled: moveLoading
                    },
                    {
                        label: "Move",
                        variant: "primary",
                        action: "custom",
                        onClick: handleMoveToBottom,
                        disabled: moveLoading,
                        loadingLabel: "Moving..."
                    }
                ]}
            >
                <div className="text-center py-3">
                    <p className="mb-2">
                        Are you sure you want to move <strong>{selectedRows.length}</strong> SB number(s) to the bottom?
                    </p>
                    <p className="text-muted small mb-0">
                        The selected records will be moved to the lowest available PortCode in the hierarchy.
                    </p>
                </div>
            </DynamicModal>
        </>
    );
}

export default PortWiseTab;